import { useCallback, useEffect, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  TiendaPerfectaVan,
  TiendaPerfectaVansResponse,
} from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

interface UseVansParams {
  distributorId: string | undefined;
  includeInactive: boolean;
}

interface UseVansReturn {
  loading: boolean;
  vans: TiendaPerfectaVan[];
}

/**
 * Camionetas del distribuidor seleccionado. El endpoint no pagina y el orden lo
 * fija el servidor por placa, así que la vista solo pide y pinta.
 */
export function useVans({
  distributorId,
  includeInactive,
}: UseVansParams): UseVansReturn {
  const { message } = App.useApp();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [vans, setVans] = useState<TiendaPerfectaVan[]>([]);

  const fetchVans = useCallback(async () => {
    if (!distributorId) {
      setVans([]);
      return;
    }

    setLoading(true);

    const response = await TiendaPerfectaService.getVans({
      distributorId,
      includeInactive,
    });

    if (response.success) {
      setVans((response.data as TiendaPerfectaVansResponse)?.vans ?? []);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t('scenes.vans.scene.errors.fetch_vans'),
        3.5
      );
      setVans([]);
    }

    setLoading(false);
  }, [distributorId, includeInactive, message, t]);

  useEffect(() => {
    fetchVans();
  }, [fetchVans]);

  return { loading, vans };
}
